import { useState } from 'react';
import { Link, Outlet, useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { planMenuLabel, plansUrl } from '../lib/plan';
import { Bell, ChevronDown, CircleUser } from 'lucide-react';
import { useMe } from './me';
import { headerRegistry } from './slots';
import { useRegistry } from '../hooks/useRegistry';
import { Menu } from '../ui/Menu';
import { getApi } from '../api/endpoints';
import { settingsStore } from '../state/settings';

/** Placeholder until the notifications feature registers its bell under the same id. */
function PlaceholderBell() {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      className="icon-btn"
      disabled
      aria-label={t('shell.notifications')}
      title={t('common.comingSoon')}
    >
      <Bell size={18} />
    </button>
  );
}

headerRegistry.register({ id: 'notifications', order: 10, component: PlaceholderBell });

export function HeaderSlots() {
  const slots = useRegistry(headerRegistry);
  return (
    <>
      {slots.map((s) => (
        <s.component key={s.id} />
      ))}
    </>
  );
}

export function WorkspaceSwitcher({ current }: { current?: string | undefined }) {
  const { t } = useTranslation();
  const me = useMe();
  const navigate = useNavigate();
  const workspaces = me.status === 'ready' ? me.me.workspaces : [];
  const active = workspaces.find((w) => w.id === current);
  return (
    <Menu
      label={t('shell.switchWorkspace')}
      align="start"
      buttonContent={
        <>
          <span className="ws-name">{active?.name ?? t('shell.workspaces')}</span>
          <ChevronDown size={14} />
        </>
      }
      items={[
        ...workspaces.map((w) => ({
          id: w.id,
          label: w.name,
          onSelect: () => void navigate(`/w/${w.id}`),
        })),
        { id: '__all', label: t('shell.allWorkspaces'), onSelect: () => void navigate('/') },
      ]}
    />
  );
}

export function UserMenu() {
  const { t } = useTranslation();
  const me = useMe();
  const navigate = useNavigate();
  const name = me.status === 'ready' ? me.me.name : t('shell.offline');
  const lang = settingsStore.getState().language;
  // the plan's validity is judged at mount time (React may not read the clock during render)
  const [now] = useState(() => Date.now());
  const planLabel = me.status === 'ready' ? planMenuLabel(me.me, t, lang, now) : null;
  return (
    <Menu
      label={t('shell.userMenu')}
      buttonContent={
        <>
          <CircleUser size={18} />
          <span className="user-name">{name}</span>
        </>
      }
      items={[
        { id: 'settings', label: t('shell.settings'), onSelect: () => void navigate('/settings') },
        ...(me.status === 'ready' && me.me.platformAdmin
          ? [{ id: 'admin', label: t('shell.admin'), onSelect: () => void navigate('/admin') }]
          : []),
        // theta-portal plan (U78): opens Central's plans
        ...(me.status === 'ready' && planLabel !== null
          ? [
              {
                id: 'plan',
                label: planLabel,
                onSelect: () => {
                  const url = plansUrl(me.me);
                  if (url) window.open(url, '_blank', 'noopener');
                },
              },
            ]
          : []),
        {
          id: 'lang',
          label: lang === 'de' ? 'English' : 'Deutsch',
          onSelect: () => settingsStore.getState().setLanguage(lang === 'de' ? 'en' : 'de'),
        },
        {
          id: 'logout',
          label: t('shell.logout'),
          disabled: me.status !== 'ready',
          onSelect: () => {
            void getApi()
              .logout()
              .finally(() => window.location.assign('/'));
          },
        },
      ]}
    />
  );
}

export function AppHeader() {
  const { t } = useTranslation();
  const { wsId } = useParams();
  return (
    <header className="app-header">
      <Link to="/" className="brand" aria-label={t('shell.home')}>
        <svg viewBox="0 0 32 32" width="22" height="22" aria-hidden="true">
          <rect x="2" y="6" width="28" height="20" rx="3" fill="var(--accent)" />
          <circle cx="11" cy="15" r="3.5" fill="#fff" />
          <circle cx="21" cy="15" r="3.5" fill="#fff" />
        </svg>
        <span>stageplottter</span>
      </Link>
      <WorkspaceSwitcher current={wsId} />
      <div className="spacer" />
      <HeaderSlots />
      <UserMenu />
    </header>
  );
}

export function AppShell() {
  const { t } = useTranslation();
  return (
    <div className="app-shell">
      <a className="skip-link" href="#main">
        {t('shell.skipToContent')}
      </a>
      <AppHeader />
      <main id="main" className="app-main">
        <Outlet />
      </main>
    </div>
  );
}
